import {
  ReactNode,
  useEffect,
  useRef,
  createRef,
  useState,
  useCallback,
} from 'react'
import styled, { css } from 'styled-components'
import { Box } from '../../../shared_components/UI/Box'
import { isInView } from '../../../utils/isInView'
import useTimeout from '../../../hooks/useTimeout'

interface Props {
  items: ReactNode[]
  cardWidth: number
}

const Outer = styled(Box)`
  position: relative;
  z-index: 10;

  flex-direction: column;
  gap: 24px;
`

const Container = styled(Box)`
  width: 100vw;
  margin-left: -32px;
  overflow: scroll;
  padding: 0 32px;
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  position: relative;
  z-index: 10;
`

const CardContainer = styled(Box)<{ cardWidth: number; isActive: boolean }>`
  flex: 1;
  min-width: ${({ cardWidth }) => cardWidth}px;
  margin-right: 40px;
  opacity: ${({ isActive }) => (isActive ? 1 : 0.5)};
`

const Arrows = styled(Box)`
  z-index: 10;
`

const Arrow = styled(Box)<{ isLeft?: boolean; disabled: boolean }>`
  justify-content: center;
  align-items: center;
  border: 1px solid #888a8c;
  flex: 0;
  min-width: 44px;
  height: 44px;
  margin-right: ${({ isLeft }) => (isLeft ? 16 : 0)}px;
  ${({ isLeft }) =>
    isLeft &&
    css`
      transform: scaleX(-1);
    `}
  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
      cursor: not-allowed;
    `}
`

const ArrowImg = styled.img``

export default function Carousel({ items, cardWidth }: Props) {
  const [showArrows, setShowArrows] = useState(false)
  const [itemRefs, setItemRefs] = useState<any[]>([])
  const [position, setPosition] = useState<'start' | 'scrolling' | 'end'>(
    'start',
  )
  const scrollRef = useRef<HTMLDivElement>(null)
  const inViewIndexRef = useRef<number>(0)
  const TOTAL_DIV_WIDTH = cardWidth + 40
  const totalItems = items.length

  const scrollToNearest = useCallback(() => {
    if (scrollRef?.current) {
      const currentScrollPos = scrollRef.current.scrollLeft
      const roughIndex = Math.round(currentScrollPos / TOTAL_DIV_WIDTH)
      scrollToPos(roughIndex * TOTAL_DIV_WIDTH)
    }
  }, [TOTAL_DIV_WIDTH])

  const checkElems = useCallback(
    (hasStopped = false) => {
      let noActive = true
      itemRefs.forEach((ref: any, i) => {
        if (ref?.current) {
          const rect = ref.current.getBoundingClientRect()
          const inView = isInView(rect, window)
          if (inView) {
            ref.current.style.opacity = 1
            if (inViewIndexRef?.current > -1) {
              inViewIndexRef.current = i
            }
            noActive = false
          } else {
            ref.current.style.opacity = 0.3
          }
        }
      })
      if (noActive && hasStopped) scrollToNearest()
    },
    [itemRefs, scrollToNearest],
  )

  const checkElemsTimer = useTimeout(() => {
    checkElems(true)
  }, 100)

  const handleScroll = useCallback(() => {
    clearTimeout(checkElemsTimer.current)
    if (scrollRef?.current) checkElems()

    const isAtStart = scrollRef.current.scrollLeft === 0
    const isAtEnd =
      scrollRef.current.scrollLeft + window.innerWidth >=
      totalItems * TOTAL_DIV_WIDTH
    if (isAtStart) setPosition('start')
    else if (isAtEnd) setPosition('end')
    else setPosition('scrolling')
  }, [TOTAL_DIV_WIDTH, checkElems, checkElemsTimer, totalItems])

  const scrollToPos = (pos: number) => {
    if (scrollRef?.current) {
      scrollRef.current.scroll({
        top: 0,
        left: pos,
        behavior: 'smooth',
      })
    }
  }

  const move = (isLeft = false) => {
    if (scrollRef.current) {
      scrollRef.current.scroll({
        top: 0,
        left: isLeft
          ? scrollRef.current.scrollLeft - TOTAL_DIV_WIDTH
          : scrollRef.current.scrollLeft + TOTAL_DIV_WIDTH,
        behavior: 'smooth',
      })
      checkElems()
    }
  }

  useEffect(() => {
    if (itemRefs?.[1]?.current) itemRefs[1].current.style.opacity = 0.3
  }, [itemRefs])

  useEffect(() => {
    setItemRefs((itemRefs) =>
      Array(items.length)
        .fill(0)
        .map((_, i) => itemRefs[i] || createRef()),
    )

    // If cards take too much space, show arrows
    const showArrows = window.innerWidth - items.length * cardWidth < 50

    setShowArrows(showArrows)
  }, [cardWidth, items])

  return (
    <Outer>
      {showArrows && (
        <Arrows jfe>
          <Arrow
            isLeft
            onClick={() => move(true)}
            disabled={position === 'start'}
          >
            <ArrowImg
              src="/img/icons/carousel-arrow-right.svg"
              alt="right arrow"
              width="13px"
              color="white"
            />
          </Arrow>
          <Arrow onClick={() => move()} disabled={position === 'end'}>
            <ArrowImg
              src="/img/icons/carousel-arrow-right.svg"
              alt="right arrow"
              width="13px"
              color="white"
            />
          </Arrow>
        </Arrows>
      )}
      <Container ref={scrollRef} onScroll={handleScroll}>
        {items.map((item, i) => (
          <CardContainer
            key={`carousel-card-${i}-${Math.random()}`}
            ref={itemRefs[i]}
            cardWidth={cardWidth}
            isActive
          >
            {item}
          </CardContainer>
        ))}
      </Container>
    </Outer>
  )
}
